import styled from '@emotion/styled';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  getRankingEffectDisplayName,
  getRuleTypeDisplayName,
  ProductRuleDto,
  RankingEffectEnum,
} from 'interfaces/apiTypes';

interface RuleListCardProps {
  rule: ProductRuleDto;
  onEdit: (rule: ProductRuleDto) => void;
  onDelete: (rule: ProductRuleDto) => void;
}

const Card = styled.div`
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: default;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #d6dee8;
    box-shadow: 0 2px 8px rgba(15, 32, 56, 0.06);
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const TitleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const StatusDot = styled.span<{ $active: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $active }) => ($active ? '#52c41a' : '#d9d9d9')};
  flex: none;
`;

const RuleName = styled.span`
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #141414;
`;

const UpdatedAt = styled.span`
  font-size: 12px;
  color: #bfbfbf;
  flex: none;
`;

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

const Chip = styled.span`
  height: 20px;
  display: inline-flex;
  align-items: center;
  padding: 0 7px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TypeChip = styled(Chip)`
  background: #fafafa;
  border: 1px solid #f0f0f0;
  color: #595959;
`;

const EffectChip = styled(Chip)<{ $effect: RankingEffectEnum }>`
  ${({ $effect }) => {
    switch (Number($effect) as RankingEffectEnum) {
      case RankingEffectEnum.Pin:
        return 'background:#fffbe6; border:1px solid #ffe58f; color:#d48806;';
      case RankingEffectEnum.SuperBoost:
        return 'background:#f9f0ff; border:1px solid #d3adf7; color:#531dab;';
      case RankingEffectEnum.Demote:
        return 'background:#fff1f0; border:1px solid #ffa39e; color:#cf1322;';
      default:
        return 'background:#f6ffed; border:1px solid #b7eb8f; color:#389e0d;';
    }
  }}
`;

const ValueChip = styled(Chip)`
  background: #e6f4ff;
  border: 1px solid #91caff;
  color: #0958d9;
  max-width: none;
  white-space: nowrap;
`;

const ExcludeChip = styled(Chip)`
  background: #fff1f0;
  border: 1px solid #ffa39e;
  color: #cf1322;
  max-width: none;
  white-space: nowrap;
`;

const VISIBLE_EXPR_PARTS = 3;

const formatExprPreview = (expr?: string | null) => {
  if (!expr?.trim()) {
    return { label: '—', full: '' };
  }

  const parts = expr
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length <= VISIBLE_EXPR_PARTS) {
    return { label: parts.join(', '), full: parts.join(', ') };
  }

  const visible = parts.slice(0, VISIBLE_EXPR_PARTS).join(', ');
  const remaining = parts.length - VISIBLE_EXPR_PARTS;
  return {
    label: `${visible} +${remaining}`,
    full: parts.join(', '),
  };
};

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px dashed #f0f0f0;
`;

const ActivePeriod = styled.span`
  font-size: 12px;
  color: #8c8c8c;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Actions = styled.div`
  display: flex;
  gap: 2px;
  flex: none;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 5px;
  background: transparent;
  color: #8c8c8c;
  cursor: pointer;
  padding: 0;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: #f0f5fb;
    color: #14395e;
  }

  &.danger:hover {
    background: #fff1f0;
    color: #cf1322;
  }
`;

const parseValidDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  // Ignore default/unset DateTime values from the API (e.g. 0001-01-01).
  if (Number.isNaN(date.getTime()) || date.getFullYear() < 1970) {
    return null;
  }
  return date;
};

const formatDate = (value?: string | null) => {
  const date = parseValidDate(value);
  if (!date) return '—';
  return date.toLocaleDateString('sv-SE');
};

const formatModifiedDate = (rule: ProductRuleDto) => {
  const date = parseValidDate(rule.modifiedAt) ?? parseValidDate(rule.createdAt);
  if (!date) return '—';
  return date.toLocaleDateString('sv-SE');
};

const isRuleActive = (rule: ProductRuleDto) => {
  const now = Date.now();
  const start = rule.startDate ? new Date(rule.startDate).getTime() : null;
  const end = rule.endDate ? new Date(rule.endDate).getTime() : null;
  if (start != null && !Number.isNaN(start) && now < start) return false;
  if (end != null && !Number.isNaN(end) && now > end) return false;
  return true;
};

export const RuleListCard = ({ rule, onEdit, onDelete }: RuleListCardProps) => {
  const active = isRuleActive(rule);
  const modifiedAt = formatModifiedDate(rule);
  const activePeriod = `${formatDate(rule.startDate)} – ${formatDate(rule.endDate)}`;
  const valuePreview = formatExprPreview(rule.expr);
  const excludePreview = formatExprPreview(rule.excludeExpr);

  return (
    <Card>
      <TitleRow>
        <TitleLeft>
          <StatusDot $active={active} title={active ? 'Aktiv' : 'Inaktiv'} />
          <RuleName title={rule.name}>{rule.name}</RuleName>
        </TitleLeft>
        <UpdatedAt title="Senast ändrad">{modifiedAt}</UpdatedAt>
      </TitleRow>

      <TagsRow>
        <TypeChip>{getRuleTypeDisplayName(Number(rule.ruleType))}</TypeChip>
        <EffectChip $effect={Number(rule.effect) as RankingEffectEnum}>
          {getRankingEffectDisplayName(Number(rule.effect))}
        </EffectChip>
        <ValueChip title={valuePreview.full}>{valuePreview.label}</ValueChip>
        {rule.excludeExpr ? (
          <ExcludeChip title={`Exkludera: ${excludePreview.full}`}>
            − {excludePreview.label}
          </ExcludeChip>
        ) : null}
      </TagsRow>

      <Footer>
        <ActivePeriod title={activePeriod}>{activePeriod}</ActivePeriod>
        <Actions>
          <IconButton type="button" title="Redigera" onClick={() => onEdit(rule)}>
            <EditOutlined style={{ fontSize: 14 }} />
          </IconButton>
          <IconButton
            type="button"
            className="danger"
            title="Ta bort"
            onClick={() => onDelete(rule)}
          >
            <DeleteOutlined style={{ fontSize: 14 }} />
          </IconButton>
        </Actions>
      </Footer>
    </Card>
  );
};
